import type { AncestorGraph } from '../domain/AncestorGraph';
import type { DotCell, PersonAtPlace } from '../domain/DotCell';
import type { Person } from '../domain/Person';
import { cellKey } from '../utils/CellKey';
import { createLogger } from '../utils/logger';
import { personDisplayName } from '../utils/PersonName';

const logger = createLogger('DotAccumulatorService');

/** One entry per person per branch in a cell; keeps the person's earliest event year. */
function addPerson(
  cell: DotCell,
  branchId: string,
  person: Person,
  year: number | undefined,
): void {
  const people = cell.people ?? new Map<string, PersonAtPlace[]>();
  cell.people = people;
  const list = people.get(branchId) ?? [];
  people.set(branchId, list);

  const existing = list.find((p) => p.personId === person.id);
  if (existing === undefined) {
    const entry: PersonAtPlace = { personId: person.id, name: personDisplayName(person) };
    if (year !== undefined) {
      entry.year = year;
    }
    list.push(entry);
  } else if (year !== undefined && (existing.year === undefined || year < existing.year)) {
    existing.year = year;
  }
}

/** Clusters coordinates to ~25 km regional bins (shared with ArcLayer). */
const locationKey = cellKey;

export class DotAccumulatorService {
  /**
   * Produces a dot-weight map from the ancestor graph.
   *
   * Walks only graph.persons and their events — NEVER reads childBirthsByParent.
   * Child-birth locations are migration evidence, not dot mass.
   *
   * Skips events without geocoded coordinates (lat/lng) and undated events.
   */
  accumulate(graph: AncestorGraph, assignments: Map<string, string>): Map<string, DotCell> {
    logger.info(`DotAccumulator started — ${graph.persons.size} persons in graph`);

    const dotMap = new Map<string, DotCell>();
    // Dedup brick-wall persons at the same clustered location (one count per person per cell)
    const brickWallSeen = new Set<string>();
    // Distinct places per cell, for the dot anchor
    const placesByCell = new Map<string, Map<string, [number, number]>>();
    let totalEvents = 0;

    for (const person of graph.persons.values()) {
      const branchId = assignments.get(person.id);

      for (const event of person.events) {
        const loc = event.location;
        if (
          loc === undefined ||
          loc.geocoded !== true ||
          loc.lat === undefined ||
          loc.lng === undefined
        ) {
          continue;
        }

        // Undated events contribute no dot mass in any time window
        let decade: number;
        if (event.decade !== undefined) {
          decade = event.decade;
        } else if (event.year !== undefined) {
          decade = Math.floor(event.year / 10) * 10;
        } else {
          continue;
        }

        const key = locationKey(loc.lat, loc.lng);
        const places = placesByCell.get(key) ?? new Map<string, [number, number]>();
        places.set(`${loc.lat.toString()},${loc.lng.toString()}`, [loc.lat, loc.lng]);
        placesByCell.set(key, places);

        let cell = dotMap.get(key);
        if (cell === undefined) {
          cell = {
            weightByDecade: new Map(),
            branchCounts: new Map(),
            brickWallCount: 0,
            placeName: loc.raw,
            people: new Map(),
          };
          dotMap.set(key, cell);
        }

        cell.weightByDecade.set(decade, (cell.weightByDecade.get(decade) ?? 0) + 1);

        if (loc.approximate === true) {
          cell.approximate = true;
        }
        if (loc.unverified === true) {
          cell.unverified = true;
        }

        if (branchId !== undefined) {
          cell.branchCounts.set(branchId, (cell.branchCounts.get(branchId) ?? 0) + 1);
          addPerson(cell, branchId, person, event.year);
        }

        // Brick-wall: once per person per clustered location, not per event
        if (person.isBrickWall) {
          const bwKey = `${person.id}:${key}`;
          if (!brickWallSeen.has(bwKey)) {
            brickWallSeen.add(bwKey);
            cell.brickWallCount++;
          }
        }

        totalEvents++;
      }
    }

    // Draw each dot at the average of the distinct places in its cell, not the grid point,
    // so lines drawn to the same anchor meet the dot (P-15f).
    for (const [key, cell] of dotMap) {
      const pts = [...(placesByCell.get(key)?.values() ?? [])];
      if (pts.length > 0) {
        cell.anchor = {
          lat: pts.reduce((s, p) => s + p[0], 0) / pts.length,
          lng: pts.reduce((s, p) => s + p[1], 0) / pts.length,
        };
      }
    }

    logger.info(
      `DotAccumulator complete — ${dotMap.size} location cells, ${totalEvents} total events`,
    );
    return dotMap;
  }
}
